/**
 * services/keyBinds.ts · 全局键盘快捷键 v339 (P0 老员工效率 3x)
 *
 * 默认快捷键 (复用 v200 v159 TN_KEYBINDS):
 *   ⌘K / Ctrl+K     → 全局搜索 palette
 *   ⌘Enter          → 提交当前 form
 *   Esc             → 关 modal/palette/drawer
 *   /               → 聚焦顶部搜索框
 *
 * 智能 inEditable 判定:
 *   - 在 input/textarea: 仅 ⌘K / ⌘Enter / Esc 生效, 其他通过
 *   - 在普通区域: 全开放
 */

type Handler = (e: KeyboardEvent) => void;
const handlers: Record<string, Handler> = {};

function isEditable(target: EventTarget | null): boolean {
  if (!target) return false;
  const el = target as HTMLElement;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  // role=textbox/searchbox/combobox
  const role = el.getAttribute?.('role');
  if (role && ['textbox', 'searchbox', 'combobox'].includes(role)) return true;
  return false;
}

export function registerKey(combo: string, handler: Handler): void {
  handlers[combo.toLowerCase()] = handler;
}

export function initKeyBinds(): void {
  document.addEventListener('keydown', (e) => {
    const cmdOrCtrl = e.metaKey || e.ctrlKey;
    const editable = isEditable(e.target);

    // ⌘K / Ctrl+K → palette (always)
    if (cmdOrCtrl && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      handlers['cmd+k']?.(e);
      return;
    }
    // Esc → close modal
    if (e.key === 'Escape') {
      handlers['esc']?.(e);
      return;
    }
    // ⌘+Enter → submit form
    if (cmdOrCtrl && e.key === 'Enter') {
      e.preventDefault();
      handlers['cmd+enter']?.(e);
      return;
    }
    // / → focus search (only when not editing)
    if (e.key === '/' && !editable) {
      e.preventDefault();
      handlers['/']?.(e);
      return;
    }
  });
}

// 默认绑定
initKeyBinds();

registerKey('cmd+k', () => {
  // 触发 CommandPalette
  document.dispatchEvent(new CustomEvent('tn-open-palette'));
});

registerKey('esc', () => {
  // 关闭最顶 modal/drawer/palette
  const modals = document.querySelectorAll('.tn-modal-backdrop, .tn-drawer-backdrop, #tn-cmdk-backdrop, #tn-camera-backdrop');
  if (modals.length > 0) {
    const top = modals[modals.length - 1] as HTMLElement;
    top.remove();
    return;
  }
});

registerKey('cmd+enter', () => {
  // 找当前焦点所在 form 的 submit button
  const active = document.activeElement as HTMLElement;
  if (!active) return;
  const form = active.closest('form') || active.closest('.tn-form');
  if (!form) return;
  const btn = form.querySelector<HTMLButtonElement>('button[type="submit"], button[data-tn-submit]');
  btn?.click();
});

registerKey('/', () => {
  // focus 顶 NAV 搜索框
  const searchInput = document.querySelector<HTMLInputElement>('input[type="search"], input[placeholder*="搜索"], #tn-nav-search');
  searchInput?.focus();
});
